import { useMemo, useState } from 'react';

import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Link,
  Stack,
  type SxProps,
  Typography,
} from '@mui/material';
import { REALADVISOR_TENANT_ID } from '@realadvisor/constants';
import { CreditCard, ExternalLink } from 'lucide-react';

import type { GetUserQuery } from '@graphql/__generated__/graphql';
import { fetchApiQuery } from '@graphql/ApolloHasuraWrapper';
import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import {
  type RaSelectOption,
  SelectField,
} from '@modules/_shared/components/form/RaSelect';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { toHumanReadableDate } from '@modules/_shared/utils/formatting';
import {
  getSubscriptionCurrency,
  getSubscriptionStatusColor,
  getSubscriptionStatusLabel,
} from '@modules/_shared/utils/subscription';
import { UPDATE_USER_AUTO_ASSIGN_LEADS } from '@modules/users/userQueries';
import * as config from '@shared/config';
import { useLocale } from '@shared/hooks/locale';
import { formatPrice } from '@shared/utils/format-price';
import { toGlobalId } from '@shared/utils/global-id';

const smallChipSx: SxProps = {
  height: 20,
  fontSize: 11,
  '& .MuiChip-label': { px: 0.75 },
};

type UserSubscriptionCardProps = {
  user: NonNullable<GetUserQuery['users_by_pk']>;
};

type Subscription = NonNullable<
  NonNullable<GetUserQuery['users_by_pk']>['subscription_emails']
>[number]['subscriptions'][number];

const STATUS_ORDER = [
  'active',
  'trialing',
  'past_due',
  'unpaid',
  'incomplete',
  'incomplete_expired',
  'canceled',
];

const SubscriptionRow = ({ subscription }: { subscription: Subscription }) => {
  const { t, locale } = useLocale();

  return (
    <Stack sx={{ gap: 0.75 }}>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 24 }}
        >
          <Box
            sx={{
              color: 'text.secondary',
              display: 'flex',
              flexShrink: 0,
              width: 16,
            }}
          >
            <CreditCard size={16} />
          </Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {subscription.plan_name ?? t('Subscription')}
          </Typography>
        </Box>
        <Chip
          label={getSubscriptionStatusLabel(subscription.status, t)}
          size="small"
          color={getSubscriptionStatusColor(subscription.status)}
          sx={smallChipSx}
        />
      </Stack>

      <Stack sx={{ pl: 3, gap: 0.25 }}>
        {subscription.total_amount > 0 && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {formatPrice(
              subscription.total_amount / 100,
              locale,
              getSubscriptionCurrency(subscription),
            )}
          </Typography>
        )}
        {subscription.current_period_start &&
          subscription.current_period_end && (
            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {toHumanReadableDate(locale, subscription.current_period_start, {
                month: 'short',
              })}
              {' → '}
              {toHumanReadableDate(locale, subscription.current_period_end, {
                month: 'short',
              })}
            </Typography>
          )}
      </Stack>
    </Stack>
  );
};

const StripeLink = ({ customer }: { customer: string }) => {
  const { t } = useLocale();
  return (
    <Link
      href={`https://dashboard.stripe.com/customers/${customer}`}
      target="_blank"
      rel="noopener"
      underline="none"
      color="text.primary"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexShrink: 0,
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {t('Stripe')}
      </Typography>
      <ExternalLink size={14} />
    </Link>
  );
};

const AutoAssignLeads = ({
  userId,
  autoAssignLeads,
  action,
}: {
  userId: string;
  autoAssignLeads: boolean | null | undefined;
  action?: React.ReactNode;
}) => {
  const { t } = useLocale();
  const [updateUser, { loading, error, reset }] = useMutation(
    UPDATE_USER_AUTO_ASSIGN_LEADS,
  );

  const value =
    autoAssignLeads == null ? 'null' : autoAssignLeads ? 'true' : 'false';

  const options: RaSelectOption[] = [
    { value: 'null', label: t('Use subscription status') },
    { value: 'true', label: t('Always') },
    { value: 'false', label: t('Never') },
  ];

  return (
    <Stack sx={{ gap: 0.75 }}>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
      >
        <RaLabel label={t('Auto-assign leads')} sx={{ mb: 0 }} />
        {action}
      </Stack>
      <SelectField
        field={{
          value,
          onChange: event => {
            const next = event.target.value as string;
            void updateUser({
              variables: {
                id: userId,
                autoAssignLeads: next === 'null' ? null : next === 'true',
              },
            });
          },
          onBlur: () => {},
          name: 'auto_assign_leads',
          ref: () => {},
        }}
        options={options}
        disabled={loading}
        loading={loading}
      />

      <MutationErrorSnackbar error={error ?? null} onClose={reset} />
    </Stack>
  );
};

export const UserSubscriptionCard = ({ user }: UserSubscriptionCardProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const [showMore, setShowMore] = useState(false);

  const subscriptions = useMemo(() => {
    const all = (user.subscription_emails ?? []).flatMap(
      email => email.subscriptions ?? [],
    );
    const byId = new Map<string, Subscription>(all.map(s => [s.id, s]));
    const statusRank = (status: string) => {
      const index = STATUS_ORDER.indexOf(status);
      return index === -1 ? STATUS_ORDER.length : index;
    };
    const endTime = (subscription: Subscription) => {
      const time = subscription.current_period_end
        ? new Date(subscription.current_period_end).getTime()
        : 0;
      return Number.isNaN(time) ? 0 : time;
    };
    return [...byId.values()].sort((a, b) => {
      const statusDiff = statusRank(a.status) - statusRank(b.status);
      if (statusDiff !== 0) {
        return statusDiff;
      }
      return endTime(b) - endTime(a);
    });
  }, [user.subscription_emails]);

  const isAdmin = me?.is_admin ?? false;
  const canEdit = me?.id === user.id;
  const showAutoAssign = isAdmin && me?.tenant_id !== REALADVISOR_TENANT_ID;

  const stripeCustomer =
    subscriptions.find(subscription => subscription.customer != null)
      ?.customer ?? null;
  const stripeLink =
    isAdmin && stripeCustomer != null ? (
      <StripeLink customer={stripeCustomer} />
    ) : null;

  const hasSubscriptions = subscriptions.length > 0;

  if (!hasSubscriptions && !showAutoAssign) {
    return null;
  }

  const editSubscription = async () => {
    if (me?.tenant_id == null) {
      return;
    }
    const url = new URL(
      '/api/stripe/create-customer-portal-session',
      config.api_origin,
    );
    url.searchParams.set('returnUrl', window.location.href);
    url.searchParams.set('tenantId', toGlobalId('Tenant', me.tenant_id));
    const response = await fetchApiQuery(url, {
      method: 'POST',
      credentials: 'include',
    });
    if (response.ok) {
      const result: { redirect_uri?: string } = await response.json();
      if (result.redirect_uri != null) {
        window.location.href = result.redirect_uri;
      }
    } else {
      console.error('create-customer-portal-session failed', response.status);
    }
  };

  const visibleSubscriptions = showMore
    ? subscriptions
    : subscriptions.slice(0, 1);

  return (
    <ListingAccordion title={t('Subscription')} defaultExpanded>
      <Stack sx={{ gap: 1.5 }}>
        {hasSubscriptions && (
          <Stack sx={{ gap: 1.5 }} divider={<Divider flexItem />}>
            {visibleSubscriptions.map(subscription => (
              <SubscriptionRow
                key={subscription.id}
                subscription={subscription}
              />
            ))}
          </Stack>
        )}

        {subscriptions.length > 1 && (
          <Button
            size="small"
            onClick={() => setShowMore(prev => !prev)}
            sx={{ px: 0, minWidth: 0 }}
          >
            {showMore ? t('Show less') : t('Show more')}
          </Button>
        )}

        {(showAutoAssign || stripeLink != null || canEdit) && (
          <>
            {hasSubscriptions && <Divider />}
            {showAutoAssign ? (
              <AutoAssignLeads
                userId={user.id}
                autoAssignLeads={user.auto_assign_leads}
                action={stripeLink}
              />
            ) : (
              stripeLink && (
                <Stack direction="row" sx={{ justifyContent: 'flex-end' }}>
                  {stripeLink}
                </Stack>
              )
            )}
            {canEdit && (
              <Button fullWidth variant="contained" onClick={editSubscription}>
                {t('Edit subscription')}
              </Button>
            )}
          </>
        )}
      </Stack>
    </ListingAccordion>
  );
};
